const GEMINI_API_BASE = 'https://generativelanguage.googleapis.com/v1beta/models';

/** Models with free-tier quota as of 2026 — tried in order on quota errors. */
const MODEL_FALLBACKS = [
  'gemini-2.5-flash-lite',
  'gemini-2.5-flash',
  'gemini-1.5-flash',
];

function resolveModelList() {
  const preferred = import.meta.env.VITE_GEMINI_MODEL?.trim();
  if (!preferred) return MODEL_FALLBACKS;
  return [preferred, ...MODEL_FALLBACKS.filter((model) => model !== preferred)];
}

export function getGeminiApiKey() {
  return import.meta.env.VITE_GEMINI_API_KEY?.trim() || '';
}

export function isGeminiConfigured() {
  return Boolean(getGeminiApiKey());
}

function parseGeminiError(detail, status) {
  if (status === 403) {
    return 'API key non valida o progetto non autorizzato';
  }

  if (status === 429) {
    if (/limit:\s*0/i.test(detail)) {
      return (
        'Quota Gemini non attiva (limite 0). Su Google AI Studio collega un account di fatturazione al progetto della API key — resti nel free tier se non superi i limiti. Poi crea una nuova API key e riprova.'
      );
    }
    return 'Troppe richieste — attendi qualche secondo e riprova';
  }

  return `Errore Gemini (${status})${detail ? `: ${detail.slice(0, 160)}` : ''}`;
}

function extractGroundingSources(data) {
  const chunks = data?.candidates?.[0]?.groundingMetadata?.groundingChunks ?? [];
  const seen = new Set();
  const sources = [];

  for (const chunk of chunks) {
    const uri = chunk?.web?.uri?.trim();
    if (!uri || seen.has(uri)) continue;
    seen.add(uri);
    sources.push({
      title: chunk?.web?.title?.trim() || uri,
      uri,
    });
  }

  return sources;
}

async function requestGemini({ apiKey, model, systemPrompt, contents, signal, googleSearch = false }) {
  const url = `${GEMINI_API_BASE}/${model}:generateContent?key=${encodeURIComponent(apiKey)}`;
  const body = {
    systemInstruction: { parts: [{ text: systemPrompt }] },
    contents,
    generationConfig: {
      temperature: googleSearch ? 0.78 : 0.72,
      maxOutputTokens: 1536,
    },
  };

  if (googleSearch) {
    body.tools = [{ google_search: {} }];
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });

  const detail = await response.text().catch(() => '');

  if (!response.ok) {
    const err = new Error(parseGeminiError(detail, response.status));
    err.status = response.status;
    err.detail = detail;
    err.model = model;
    throw err;
  }

  const data = JSON.parse(detail);
  const text = data?.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
  if (!text) {
    const reason = data?.candidates?.[0]?.finishReason;
    if (reason === 'SAFETY') {
      throw new Error('Risposta bloccata dai filtri di sicurezza');
    }
    throw new Error('Nessuna risposta dal modello');
  }

  return {
    text,
    sources: googleSearch ? extractGroundingSources(data) : [],
  };
}

function shouldTryNextModel(error) {
  if (error?.status !== 429) return false;
  const detail = error.detail ?? error.message ?? '';
  return /limit:\s*0/i.test(detail) || /model:\s*gemini-2\.0/i.test(detail);
}

/**
 * @param {{ systemPrompt: string, messages: Array<{ role: 'user' | 'assistant', content: string }>, signal?: AbortSignal, googleSearch?: boolean }} opts
 * @returns {Promise<{ text: string, sources: Array<{ title: string, uri: string }> }>}
 */
export async function sendGeminiChat({ systemPrompt, messages, signal, googleSearch = false }) {
  const apiKey = getGeminiApiKey();
  if (!apiKey) {
    throw new Error('API key Gemini non configurata. Aggiungi VITE_GEMINI_API_KEY nel file .env');
  }

  const contents = messages.map((message) => ({
    role: message.role === 'assistant' ? 'model' : 'user',
    parts: [{ text: message.content }],
  }));

  const models = resolveModelList();
  let lastError = null;

  for (let index = 0; index < models.length; index += 1) {
    const model = models[index];
    try {
      return await requestGemini({ apiKey, model, systemPrompt, contents, signal, googleSearch });
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      lastError = error;
      const hasNext = index < models.length - 1;
      if (hasNext && shouldTryNextModel(error)) continue;
      throw error;
    }
  }

  throw lastError ?? new Error('Errore Gemini sconosciuto');
}
